import React, { useState, useRef, useEffect, useContext } from "react"
import styles from "./drag-upload.module.css"
import Icon from "../icon"
import { useSelector } from "../../stores/dashboard.store"
import { uploadMedia } from "../../api-client/data"
import RunBlueprintContext from "../../context/run-blueprint.context"

const useSetIsLoading = (providedFn) => {
    const { setIsLoading } = useContext(RunBlueprintContext)

    if (providedFn === undefined) {
        return setIsLoading
    }
    return providedFn
}

const DragUploadInput = ({
    onChange,
    accept,
    value,
    setValidationError,
    setLoading = undefined
}) => {
    const { plan, activeProject } = useSelector()    
    const setIsLoading = useSetIsLoading(setLoading)

    const [dragOver, setDragOver] = useState(false)
    const fileInputRef = useRef(null)

    const [files, setFiles] = useState(value || [])
    const [fileNames, setFileNames] = useState({})

    useEffect(() => {
        setFiles(value || [])
    }, [value])

    const handleDragOver = (event) => {
        event.preventDefault()
        setDragOver(true)
    }

    const handleDragLeave = (event) => {
        event.preventDefault()
        setDragOver(false)
    }

    const onUpload = async (uploadFiles) => {
        const values = []
        const acceptStart = accept.includes("/") ? accept.split('/')[0] : ""

        for (const file of uploadFiles) {
            if ((file.size / 1000) / 1000 >= plan?.maxSizeInputVideo) {
                setValidationError({ message: `File exceeds max size of ${plan?.maxSizeInputVideo} MB` })
                return
            }

            if (acceptStart.length > 0 && !file.type.startsWith(acceptStart)) {
                setValidationError({ message: `Unsupported file type: ${file.type}` })
                return
            }

            setIsLoading(true)
            try {
                const fileid = await uploadMedia(file, activeProject?.id)
                values.push(`media:${fileid}`)

                setFileNames((prev) => ({
                    ...prev,
                    [`media:${fileid}`]: file?.name,
                }))
            } catch (error) {
                if ((error?.message?.length || 0) > 0) {
                    setValidationError({ message: error.message })
                } else {
                    setValidationError({ message: `Could not upload media, try again later.` })
                }                
            }
            setIsLoading(false)
        }

        onChange(files.concat(values))
    }

    const handleDrop = (event) => {
        event.preventDefault()
        setDragOver(false)
        const transferFiles = event.dataTransfer.files
        if (transferFiles?.length > 0) {
            onUpload(transferFiles)
        }
    }

    const handleClick = () => {
        fileInputRef.current.click()
    }

    const handleChange = (event) => {
        const eventFiles = event.target.files
        if (eventFiles?.length > 0) {
            onUpload(eventFiles)
        }
    }

    const clearFile = (file) => {
        onChange((prev) => prev.filter(x => x !== file))
    }

    return (
        <>
            <div
                className={`${styles.imageDrop} ${dragOver ? styles.dragOver : ""}`}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                Click to upload a file or drag and drop
                <input
                    accept={accept}
                    type="file"
                    ref={fileInputRef}
                    onChange={handleChange}
                    style={{ display: "none" }}
                />
            </div>
            {
                files?.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                        {
                            files.map(file => (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                            maxWidth: "300px",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            color: 'var(--text-primary)'
                }}
                                    >
                                        {fileNames[file] || file?.name || file.replace("media:", "")}
                                    </div>
                                    <div onClick={() => clearFile(file)} style={{ cursor: "pointer", fontSize: 5 }}>
                                        <Icon type="close" style={{color:'var(--text-primary)'}} />
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                )
            }
        </>
    )
}

export default DragUploadInput
